import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, PlayCircle, CheckCircle, FileText, 
  ExternalLink, HelpCircle, Clock, ChevronRight
} from 'lucide-react';

const API = API_BASE;

export default function TrainingModule() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/training/modules/${moduleId}`);
      setModule(response.data);
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setLoading(false);
    }
  };

  const lang = user?.training_language || 'en';
  const t = (en, es) => lang === 'es' ? es : en;

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return PlayCircle;
      case 'text': return FileText;
      case 'quiz': return HelpCircle;
      case 'link': return ExternalLink;
      default: return FileText;
    }
  };

  const getLessonStatus = (lesson) => {
    const progress = lesson.user_progress || {};
    if (progress.completed) return 'completed';
    if (progress.video_progress > 0 || progress.time_spent_minutes > 0) return 'in_progress';
    return 'not_started';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('Module not found', 'Módulo no encontrado')}</p>
        <Button onClick={() => navigate('/training')} className="mt-4">
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
      </div>
    );
  }

  const modProgress = module.user_progress || {};
  const completedLessons = module.lessons?.filter(l => getLessonStatus(l) === 'completed').length || 0;

  return (
    <div className="space-y-6 p-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/training')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('Back to Training', 'Volver a Capacitación')}
      </Button>

      {/* Module Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <Badge variant="outline" className="mb-2">
                {t('Module', 'Módulo')} {module.module_number}
              </Badge>
              <CardTitle className="text-2xl">
                {lang === 'es' ? module.title_es : module.title_en}
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                {lang === 'es' ? module.description_es : module.description_en}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">
                {t('Estimated time', 'Tiempo estimado')}
              </p>
              <p className="text-2xl font-bold">{module.estimated_hours}h</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>{t('Progress', 'Progreso')}</span>
                <span>{completedLessons} / {module.lessons?.length || 0} {t('lessons', 'lecciones')}</span>
              </div>
              <Progress 
                value={(completedLessons / (module.lessons?.length || 1)) * 100} 
                className="h-2"
              />
            </div>
            {modProgress.completion_percentage >= 100 && (
              <Badge variant="success" className="text-lg px-4 py-2">
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('Completed', 'Completado')}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lessons List */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">
          {t('Lessons', 'Lecciones')}
        </h2>
        
        {module.lessons?.map((lesson, index) => {
          const status = getLessonStatus(lesson);
          const LessonIcon = getLessonIcon(lesson.type);
          const lessonProgress = lesson.user_progress || {};
          
          // All lessons are unlocked for everyone — sequential gating removed per request
          const isLocked = false;

          return (
            <Card 
              key={lesson.id}
              className={`cursor-pointer transition-all ${
                status === 'completed' ? 'border-green-500/30 bg-green-50/20' :
                status === 'in_progress' ? 'border-primary/30 bg-primary/5' :
                isLocked ? 'opacity-50' : 'hover:border-primary/30'
              }`}
              onClick={() => !isLocked && navigate(`/training/lesson/${lesson.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Lesson Number & Icon */}
                  <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center
                    ${status === 'completed' ? 'bg-green-500 text-white' :
                      status === 'in_progress' ? 'bg-primary/20 text-primary' :
                      'bg-muted text-muted-foreground'}
                  `}>
                    {status === 'completed' ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <LessonIcon className="h-6 w-6" />
                    )}
                  </div>

                  {/* Lesson Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {lesson.lesson_number}.
                      </span>
                      <h3 className="font-medium">
                        {lang === 'es' ? lesson.title_es : lesson.title_en}
                      </h3>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {lesson.duration_minutes} min
                      </span>
                      {lesson.type === 'video' && lessonProgress.video_progress > 0 && (
                        <span>
                          {lessonProgress.video_progress.toFixed(0)}% {t('watched', 'visto')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Arrow */}
                  {!isLocked && (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>

                {/* Video Progress Bar */}
                {lesson.type === 'video' && lessonProgress.video_progress > 0 && lessonProgress.video_progress < 100 && (
                  <Progress 
                    value={lessonProgress.video_progress} 
                    className="mt-3 h-1"
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Module Quiz Card - Open to everyone (sequential gating removed per request) */}
      {module.quiz && (() => {
        const canTake = true;
        const alreadyPassed = !!module.quiz.passed;
        return (
          <Card
            data-testid="module-quiz-card"
            className={`mt-4 transition-all ${
              alreadyPassed
                ? 'border-emerald-300 bg-emerald-50/50'
                : canTake
                  ? 'border-blue-300 bg-blue-50/50 hover:border-blue-400 cursor-pointer shadow-sm'
                  : 'opacity-60 bg-slate-50'
            }`}
            onClick={() => canTake && navigate(`/training/quiz/${module.quiz.id}`)}
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-4">
                <div className={`
                  flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center
                  ${alreadyPassed
                    ? 'bg-emerald-500 text-white'
                    : canTake
                      ? 'bg-blue-500 text-white shadow-blue-200 shadow-lg'
                      : 'bg-slate-200 text-slate-400'}
                `}>
                  {alreadyPassed ? <CheckCircle className="h-6 w-6" /> : <HelpCircle className="h-6 w-6" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">
                      {t('Module Quiz', 'Quiz del Módulo')}
                    </h3>
                    {alreadyPassed && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {t('Passed', 'Aprobado')}
                      </Badge>
                    )}
                    {!canTake && !alreadyPassed && (
                      <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">
                        {t('Complete all lessons first', 'Completa todas las lecciones primero')}
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-slate-500 mt-1">
                    {lang === 'es' ? module.quiz.title_es : module.quiz.title_en}
                  </p>

                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <HelpCircle className="h-3.5 w-3.5" />
                      {module.quiz.questions_count} {t('questions', 'preguntas')}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5" />
                      {module.quiz.passing_score}% {t('to pass', 'para aprobar')}
                    </span>
                    {module.quiz.attempts > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {module.quiz.attempts}/{module.quiz.max_attempts} {t('attempts', 'intentos')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0">
                  {canTake ? (
                    <Button
                      size="sm"
                      className={alreadyPassed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/training/quiz/${module.quiz.id}`);
                      }}
                      data-testid="take-module-quiz-btn"
                    >
                      {alreadyPassed ? t('Review', 'Revisar') : t('Take Quiz', 'Tomar Quiz')}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" disabled className="text-slate-400">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
